import { generateFarmName } from "@/features/rooms/lib/nameGenerator.ts";

import { Player, RoomState } from "../server/types.ts";

interface GeoSidebarProps {
  sidebarCollapsed: boolean;
  showCode: boolean;
  setShowCode: (show: boolean) => void;
  roomId: string;
  showQuitDialog: boolean;
  setShowQuitDialog: (show: boolean) => void;
  roomState: RoomState;
  myPlayerId: string | null;
  myPlayer: Player | undefined;
  stopGame: () => void;
  contextMenu: { x: number; y: number; playerId: string } | null;
  setContextMenu: (
    menu: { x: number; y: number; playerId: string } | null,
  ) => void;
  isEditingName: boolean;
  setIsEditingName: (editing: boolean) => void;
  tempName: string;
  setTempName: (name: string) => void;
  saveName: (name: string) => void;
  myName: string;
  setShowSettings: (show: boolean) => void;
}

export const GeoSidebar = ({
  sidebarCollapsed,
  showCode,
  setShowCode,
  roomId,
  setShowQuitDialog,
  roomState,
  myPlayerId,
  myPlayer,
  stopGame,
  contextMenu,
  setContextMenu,
  isEditingName,
  setIsEditingName,
  tempName,
  setTempName,
  saveName,
  myName,
  setShowSettings,
}: GeoSidebarProps) => {
  return (
    <div
      class={`bg-base-200 border-r border-base-300 flex flex-col z-10 shadow-xl transition-all duration-300 h-full ${
        sidebarCollapsed ? "w-0" : "w-80"
      }`}
    >
      <div
        class={`flex flex-col h-full ${sidebarCollapsed ? "hidden" : "flex"}`}
      >
        <div class="p-4 border-b border-base-300 flex justify-between items-center">
          <div class="flex items-center gap-2">
            {showCode && (
              <div class="badge badge-neutral font-mono transition-all duration-300">
                {roomId}
              </div>
            )}
            <button
              type="button"
              class="btn btn-xs btn-ghost btn-circle"
              onClick={() => setShowCode(!showCode)}
            >
              {showCode
                ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                )
                : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
            </button>
          </div>
          <div class="flex gap-1">
            <button
              type="button"
              class="btn btn-sm btn-ghost btn-circle"
              aria-label="Quit Game"
              onClick={() => setShowQuitDialog(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            {roomState.state === "PLAYING" && myPlayer?.isHost && (
              <button
                type="button"
                class="btn btn-sm btn-error btn-circle"
                aria-label="Stop Game"
                onClick={stopGame}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H10a1 1 0 01-1-1v-4z"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-3 space-y-1 min-h-0">
          {roomState.players.map((p: Player) => (
            <div
              key={p.id}
              class={`flex items-center gap-2 p-2 bg-base-100/50 rounded-md border ${
                p.id === myPlayerId
                  ? "border-primary ring-1 ring-primary/20 bg-primary/5"
                  : "border-base-200"
              } ${!p.connected ? "opacity-40 grayscale" : ""} ${
                contextMenu?.playerId === p.id
                  ? "bg-base-200 border-primary"
                  : ""
              }`}
              onContextMenu={(e) => {
                const isMe = p.id === myPlayerId;
                const isOwner = roomState.ownerId === myPlayerId;
                const amHost = myPlayer?.isHost;
                // Allow menu if:
                // 1. I am Host/Owner and target is someone else (to manage them)
                // 2. I am Owner but NOT host, and target is ME (to reclaim)
                if (
                  ((amHost || isOwner) && !isMe) || (isOwner && !amHost && isMe)
                ) {
                  e.preventDefault();
                  setContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    playerId: p.id,
                  });
                }
              }}
            >
              <div
                class="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: p.color }}
              >
              </div>
              <div class="flex-1 min-w-0">
                {/* Name Editing Logic */}
                {p.id === myPlayerId && isEditingName
                  ? (
                    <div class="flex items-center gap-1">
                      <input
                        type="text"
                        class="input input-xs input-bordered w-24 px-1 text-[10px]"
                        value={tempName}
                        onInput={(e) =>
                          setTempName(
                            (e.currentTarget as HTMLInputElement).value,
                          )}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveName(tempName);
                          if (e.key === "Escape") setIsEditingName(false);
                        }}
                        autoFocus
                      />
                      <button
                        type="button"
                        class="btn btn-xs btn-ghost btn-square text-success"
                        onClick={() => saveName(tempName)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-3 w-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="btn btn-xs btn-ghost btn-square text-info"
                        title="Randomize"
                        onClick={() => setTempName(generateFarmName())}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-3 w-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="btn btn-xs btn-ghost btn-square opacity-50"
                        onClick={() => setIsEditingName(false)}
                      >
                        ✕
                      </button>
                    </div>
                  )
                  : (
                    <div class="font-bold text-xs flex items-center gap-1 group">
                      <span class="truncate">{p.name}</span>
                      {p.isHost && (
                        <span class="text-[8px] bg-warning px-1 rounded-sm text-warning-content font-black">
                          HOST
                        </span>
                      )}
                      {p.id === roomState.ownerId && !p.isHost && (
                        <span class="text-[8px] bg-base-300 px-1 rounded-sm font-black opacity-50">
                          OWNER
                        </span>
                      )}

                      {/* Edit Button for Me (Only if Authenticated) */}
                      {p.id === myPlayerId && p.isAuthenticated && (
                        <button
                          type="button"
                          class="opacity-0 group-hover:opacity-100 transition-opacity btn btn-xs btn-ghost btn-circle w-4 h-4 min-h-0"
                          onClick={() => {
                            setTempName(myName || p.name);
                            setIsEditingName(true);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-3 w-3"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                <div class="text-[10px] opacity-60">Score: {p.score}</div>
              </div>

              {roomState.state === "PLAYING" && (
                <div class="shrink-0">
                  {p.currentTargetName
                    ? (
                      <div
                        class="radial-progress text-primary text-[8px]"
                        style={{
                          "--value": p.totalTargets
                            ? (p.progress / p.totalTargets) * 100
                            : 0,
                          "--size": "1.5rem",
                          "--thickness": "2px",
                        } as Record<string, string | number>}
                      >
                        {p.totalTargets
                          ? Math.round((p.progress / p.totalTargets) * 100)
                          : 0}%
                      </div>
                    )
                    : (
                      <span class="text-[8px] opacity-40 uppercase font-black">
                        Waiting
                      </span>
                    )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Compact Settings Footer */}
        <div class="p-3 border-t border-base-200 bg-base-200/50">
          <div class="mb-3">
            <h4 class="text-[10px] font-black uppercase tracking-widest opacity-40 mb-2">
              Game Settings
            </h4>
            <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Mode</span>
                <span class="font-bold">
                  {roomState.mode === "QUIZ" ? "Quiz" : "Puzzle"}
                </span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Region</span>
                <span class="font-bold truncate max-w-[60px]">
                  {roomState.settings.regions[0] === "World"
                    ? "World"
                    : roomState.settings.regions[0]}
                </span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Limit</span>
                <span class="font-bold">{roomState.settings.limit || "∞"}</span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Penalty</span>
                <span class="font-bold">
                  {roomState.settings.incorrectPenalty || "0"}
                </span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Wrong Limit</span>
                <span class="font-bold">
                  {roomState.settings.incorrectLimit || "∞"}
                </span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Hard Mode</span>
                <span class="font-bold">
                  {roomState.settings.hardMode ? "ON" : "OFF"}
                </span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Public</span>
                <span class="font-bold">
                  {roomState.settings.isPublic ? "YES" : "NO"}
                </span>
              </div>
              <div class="flex justify-between border-b border-base-300 pb-1">
                <span class="opacity-50">Show Answer</span>
                <span class="font-bold">
                  {roomState.settings.showAnswer ? "YES" : "NO"}
                </span>
              </div>
              {roomState.settings.hasPassword && (
                <div class="col-span-2 flex items-center justify-between border-b border-base-300 pb-1">
                  <span class="opacity-50">Password</span>
                  {showCode && roomState.settings.password
                    ? (
                      <span class="font-mono font-bold text-xs">
                        {roomState.settings.password}
                      </span>
                    )
                    : <span class="badge badge-xs badge-neutral">LOCKED</span>}
                </div>
              )}
            </div>
          </div>

          {roomState.state === "LOBBY"
            ? (
              <div class="space-y-2">
                {myPlayer?.isHost
                  ? (
                    <button
                      type="button"
                      class="btn btn-primary btn-sm w-full font-black uppercase text-xs"
                      onClick={() => setShowSettings(true)}
                    >
                      Adjust Settings
                    </button>
                  )
                  : (
                    <div class="text-center py-2 animate-pulse text-[10px] uppercase font-black opacity-30 tracking-tight">
                      Waiting for host...
                    </div>
                  )}
              </div>
            )
            : (
              <div class="text-center py-1">
                <span class="text-[10px] font-black uppercase opacity-40 tracking-tighter">
                  {roomState.mode === "QUIZ"
                    ? "Find the targets!"
                    : "Build the world!"}
                </span>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
